import { ComponentProps, FunctionComponent, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Album from "@mui/icons-material/Album";
import GridView from "@mui/icons-material/GridView";
import Menu from "@mui/icons-material/Menu";
import OpenInNew from "@mui/icons-material/OpenInNew";
import People from "@mui/icons-material/People";
import Person from "@mui/icons-material/Person";
import QuestionAnswer from "@mui/icons-material/QuestionAnswer";
import Receipt from "@mui/icons-material/Receipt";
import Science from "@mui/icons-material/Science";
import TableChart from "@mui/icons-material/TableChart";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  Stack,
  Typography as T,
} from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon/SvgIcon";
import yn from "yn";
import {
  AirflowAppIcon,
  CampaignToolIcon,
  CohortExplorerAppIcon,
  FilesToolIcon,
  FliptAppIcon,
  Home,
  IncentivesToolIcon,
  ManifoldMonoBadgeIcon,
  ParticipantSearchToolIcon,
  SigmaAppIcon,
  SnowflakeAppIcon,
  SurveyToolIcon,
  TaskToolIcon,
  UploadToolIcon,
} from "~/assets/icons";
import { COLLAPSED_WIDTH } from "~/components/SideNav";
import { Badge } from "~/components/ui/badge";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import {
  FLIPT_ROUTE_KEYS,
  ROUTE_ADMIN,
  ROUTE_DEFINITIONS,
  ROUTE_INCENTIVES,
  ROUTE_OUTREACH_V2,
  ROUTE_PARTICIPANTS,
  ROUTE_SM,
  ROUTE_SURVEYS,
  ROUTE_TASKS,
  ROUTE_UPLOADS,
  ROUTE_USERS,
  ROUTE_GROUPS,
  ROUTE_WORKFLOWS,
  ROUTE_CONVERSATIONS,
  ROUTE_COST_MANAGEMENT,
  ROUTE_DATASETS,
  ROUTE_TABLES,
  FliptFlagKey,
} from "~/router/constants";
import { VIEW_SM_APP } from "~/router/permissions";
import { useVisibleRoutes } from "~/router/useVisibleRoutes";

interface NavItem {
  title: string;
  icon: typeof SvgIcon | FunctionComponent<ComponentProps<"svg">>;
  href: string;
  // `external` renders a full-navigation anchor instead of a client-side
  // RouterLink (for destinations outside the React SPA). `openInNewTab`
  // additionally opens the link in a new tab and shows the external-link icon.
  external?: boolean;
  openInNewTab?: boolean;
  requiredPermission?: string;
  requiredFeatureFlag?: boolean;
  isBeta?: boolean;
}

interface NavItemProps extends NavItem {
  closeDrawer: () => void;
}

const {
  MODE,
  VITE_COHORT_EXPLORER_ENABLED,
  VITE_CUSTOMER,
  VITE_RWB_ENABLED,
  VITE_SIGMA_URL,
  VITE_SIGMA_WORKSPACE_ID,
  VITE_SM_ENABLED,
} = import.meta.env;

export const NavItem = ({
  closeDrawer,
  title,
  icon,
  href,
  external,
  openInNewTab,
  isBeta,
}: NavItemProps) => {
  const linkProps = external
    ? { component: "a", href, ...(openInNewTab ? { rel: "noreferrer", target: "_blank" } : {}) }
    : { component: RouterLink, to: href };

  return (
    <ListItem disablePadding aria-label={`Navigate to ${title}`}>
      <Link
        {...linkProps}
        onClick={closeDrawer}
        underline="none"
        color="inherit"
        sx={{
          gap: 1.75,
          py: 1,
          px: 1.5,
          mb: 0.25,
          width: "100%",
          display: "flex",
          alignItems: "center",
          borderRadius: 1.5,
          bgcolor: "transparent",
          userSelect: "none",
          "&:hover, &:active": {
            bgcolor: "rgba(255, 255, 255, 0.1)",
            "& svg, & p": {
              opacity: 0.99,
            },
          },
        }}
      >
        <Box
          component={icon}
          sx={{
            width: 22,
            height: 22,
            opacity: 0.96,
          }}
        />
        <T
          variant="body2"
          sx={{
            opacity: 0.96,
            color: "inherit",
            wordWrap: "break-word",
            py: 0.25,
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {title}
          {isBeta && (
            <Badge
              variant="secondary"
              className={cn(
                "ml-auto border-transparent text-[var(--dt-primitives-color-brand-deep-space)] px-[4px] py-[1px]",
                "tracking-[0.25px] font-semibold text-[10px]"
              )}
            >
              Beta
            </Badge>
          )}
        </T>

        {openInNewTab && (
          <OpenInNew
            sx={{
              opacity: 0.55,
              ml: "auto",
              width: 16,
              height: 16,
            }}
          />
        )}
      </Link>
    </ListItem>
  );
};

interface NavSection {
  title?: string;
  children: NavItem[];
  hidden?: boolean;
}

const useStudyMgmtSection = (
  permissions: string[],
  fliptFlags: Record<FliptFlagKey, boolean>
): NavSection | undefined => {
  const showSm = yn(VITE_SM_ENABLED) && permissions.includes(VIEW_SM_APP);

  // All nav items - visibility is controlled by ROUTE_DEFINITIONS
  // which includes requiredPermission and requiredFeatureFlag
  const allItems = [
    {
      title: "Participants",
      href: `${ROUTE_SM}/${ROUTE_PARTICIPANTS}`,
      icon: ParticipantSearchToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_PARTICIPANTS}`],
    },
    {
      title: "Study Incentives",
      href: `${ROUTE_SM}/${ROUTE_INCENTIVES}`,
      icon: IncentivesToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_INCENTIVES}`],
    },
    {
      title: "Data Sync",
      href: `${ROUTE_SM}/${ROUTE_UPLOADS}`,
      icon: UploadToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_UPLOADS}`],
    },
    {
      title: "Survey Builder",
      href: `${ROUTE_SM}/${ROUTE_SURVEYS}`,
      icon: SurveyToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_SURVEYS}`],
    },
    {
      title: "Outreach",
      href: `${ROUTE_SM}/${ROUTE_OUTREACH_V2}`,
      icon: CampaignToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_OUTREACH_V2}`],
    },
    {
      title: "Tasks",
      href: `${ROUTE_SM}/${ROUTE_TASKS}`,
      icon: TaskToolIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_TASKS}`],
    },
    {
      title: "Study Workflows",
      href: `${ROUTE_SM}/${ROUTE_WORKFLOWS}`,
      icon: AccountTreeIcon,
      ...ROUTE_DEFINITIONS[`${ROUTE_SM}/${ROUTE_WORKFLOWS}`],
    },
    {
      title: "Study Config",
      href: import.meta.env.DEV ? "http://localhost:3100/sm-cms/admin" : "/sm-cms/admin",
      icon: TuneIcon,
      // The SM CMS is a sibling app served from the same origin, so it needs a
      // full-page anchor (`external`) but navigates in the same tab like the
      // rest of the SC nav - no new tab, no external-link icon.
      external: true,
      requiredFliptFlag: FLIPT_ROUTE_KEYS.SM_CMS,
    },
  ];

  // Filter items based on permissions and feature flags (including Flipt)
  const items = useVisibleRoutes(permissions, allItems, fliptFlags);

  if (items.length) {
    return {
      title: "Study Management",
      hidden: !showSm,
      children: items,
    };
  }
};

const useAnalysisSection = (permissions: string[]): NavSection | undefined => {
  const items = [];
  const showRwb = yn(VITE_RWB_ENABLED);

  if (showRwb) {
    items.push({
      title: "Workbenches",
      href: "/workbenches",
      icon: GridView,
    });

    if (yn(VITE_COHORT_EXPLORER_ENABLED)) {
      items.push({
        title: "Cohort Explorer",
        href: "/cohorts",
        icon: CohortExplorerAppIcon,
      });
    }
  }

  if (items.length) {
    return {
      title: "Analysis",
      children: items,
    };
  }
};

const useAdminSection = (permissions: string[]): NavSection | undefined => {
  const adminItems = useVisibleRoutes(permissions, [
    {
      title: "Users",
      href: `${ROUTE_ADMIN}/${ROUTE_USERS}`,
      icon: Person,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_USERS}`],
    },
    {
      title: "Groups",
      href: `${ROUTE_ADMIN}/${ROUTE_GROUPS}`,
      icon: People,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_GROUPS}`],
    },
    {
      title: "Cost Management",
      isBeta: true,
      href: `${ROUTE_ADMIN}/${ROUTE_COST_MANAGEMENT}`,
      icon: Receipt,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_COST_MANAGEMENT}`],
    },
    {
      title: "Conversations",
      href: `${ROUTE_ADMIN}/${ROUTE_CONVERSATIONS}`,
      icon: QuestionAnswer,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_CONVERSATIONS}`],
    },
    {
      title: "Datasets",
      href: `${ROUTE_ADMIN}/${ROUTE_DATASETS}`,
      icon: Album,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_DATASETS}`],
    },
    {
      title: "Tables",
      href: `${ROUTE_ADMIN}/${ROUTE_TABLES}`,
      icon: TableChart,
      ...ROUTE_DEFINITIONS[`${ROUTE_ADMIN}/${ROUTE_TABLES}`],
    },
  ]);

  const externalItems: NavItem[] = [];

  if (VITE_SIGMA_WORKSPACE_ID && permissions.includes("view:sigma-ui")) {
    const url = `${VITE_SIGMA_URL}/manifold/f/${VITE_SIGMA_WORKSPACE_ID}`;
    externalItems.push({
      title: "Sigma",
      href: url,
      icon: SigmaAppIcon,
      external: true,
      openInNewTab: true,
    });
  }

  if (permissions.includes("user:airflow")) {
    const hostname = location.hostname?.split(".").slice(-4).join(".");

    externalItems.push({
      title: "Airflow",
      href: `https://airflow.${hostname}`,
      icon: AirflowAppIcon,
      external: true,
      openInNewTab: true,
    });
  }

  if (yn(import.meta.env.VITE_FLIPT_UI_ENABLED) && permissions.includes("admin:feature-flag")) {
    externalItems.push({
      title: "Flipt",
      href: "/flipt-v2-writer/",
      icon: FliptAppIcon,
      external: true,
      openInNewTab: true,
    });
  }

  if (permissions.includes("view:snowflake-ui")) {
    const databaseName = MODE === "production" ? `${VITE_CUSTOMER}_prod` : `${VITE_CUSTOMER}_stage`;

    externalItems.push({
      title: "Snowflake",
      href: `https://app.snowflake.com/sciencecloud/${VITE_CUSTOMER}/#/data/databases/${databaseName.toUpperCase()}`,
      icon: SnowflakeAppIcon,
      external: true,
      openInNewTab: true,
    });
  }

  // Add Shadcn Test in development mode
  if (import.meta.env.DEV) {
    externalItems.push({
      title: "Shadcn Test",
      href: "/shadcn-test",
      icon: Science,
    });
  }

  const allItems = [...adminItems, ...externalItems];

  if (allItems.length) {
    return {
      title: "Admin",
      children: allItems,
    };
  }
};

const useFliptRouteFlags = (
  user: { email?: string } | null | undefined
): Record<FliptFlagKey, boolean> => {
  // create a list of all flipt flags that are required by routes
  const fliptFlags = Object.values(ROUTE_DEFINITIONS)
    .filter((route) => route.requiredFliptFlag)
    .map((route) => route.requiredFliptFlag);

  // create a default where each flipt flag is false
  const defaults: Record<FliptFlagKey, boolean> = fliptFlags.reduce(
    (acc, flag) => ({
      ...acc,
      [flag as FliptFlagKey]: false,
    }),
    {} as Record<FliptFlagKey, boolean>
  );

  // evaluate each flipt flag and return the result
  const sm_workflows_editor = useFliptBoolean(
    FLIPT_ROUTE_KEYS.SM_WORKFLOWS_EDITOR,
    false,
    user?.email
  );
  const sm_cms = useFliptBoolean(FLIPT_ROUTE_KEYS.SM_CMS, false, user?.email);

  return {
    ...defaults,
    [FLIPT_ROUTE_KEYS.SM_WORKFLOWS_EDITOR]: sm_workflows_editor,
    [FLIPT_ROUTE_KEYS.SM_CMS]: sm_cms,
  };
};

const useNavSections = (): NavSection[] => {
  const user = useUser();
  const permissions = user?.permissions ?? [];

  const fliptRouteFlags = useFliptRouteFlags(user);
  const studyMgmt = useStudyMgmtSection(permissions, fliptRouteFlags);
  const analysis = useAnalysisSection(permissions);
  const admin = useAdminSection(permissions);
  const defaultSection = {
    children: [{ title: "Home", icon: Home, href: "/" }],
  };

  defaultSection.children.push({
    title: "Data Browser",
    href: "/data-browser",
    icon: FilesToolIcon,
  });

  const appSections = [studyMgmt, analysis, admin].filter(
    (val) => !!val && !val.hidden
  ) as NavSection[];

  return [defaultSection, ...appSections];
};

const NavMenu = () => {
  const [open, setOpen] = useState(false);
  const navSections = useNavSections();
  const closeDrawer = () => setOpen(false);
  // Determine whether sections should be grouped with titles/sublist
  const splitSections = navSections.filter(({ title }) => title).length > 1;

  return (
    <>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={`0 0 ${COLLAPSED_WIDTH}`}
      >
        <IconButton
          data-testid="NavMenuButton"
          aria-label="Open main navigation"
          onClick={() => setOpen(true)}
        >
          <Menu fontSize="small" />
        </IconButton>
      </Box>
      <Drawer
        open={open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            bgcolor: "#201547",
            width: 275,
            borderTopRightRadius: 15,
            color: "common.white",
          },
          elevation: 6,
        }}
        ModalProps={{
          slotProps: {
            backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.2)" } },
          },
        }}
      >
        <Box position="sticky" top={0} bgcolor="inherit" zIndex={1}>
          <Box display="flex" justifyContent="center" py={2}>
            <ManifoldMonoBadgeIcon width={24} />
          </Box>
          <Divider flexItem sx={{ borderColor: "white", opacity: 0.1, mx: 3 }} />
        </Box>
        <nav aria-label="Main navigation">
          <List sx={{ px: 1.5 }}>
            {navSections.flatMap((section) => {
              if (splitSections && section.title) {
                // Add additional list nesting if there are multiple section has title
                return (
                  <Stack component="li" key={section.title} pt={1}>
                    <T
                      variant="subtitle2"
                      pb={1}
                      px={1.5}
                      color="#A182FF"
                      letterSpacing={1}
                      fontSize={11}
                      textTransform="uppercase"
                    >
                      {section.title}
                    </T>
                    <List disablePadding>
                      {section.children.map((item) => (
                        <NavItem key={item.href} closeDrawer={closeDrawer} {...item} />
                      ))}
                    </List>
                  </Stack>
                );
              }

              // Return children without nesting
              return section.children.map((item) => (
                <NavItem key={item.href} closeDrawer={closeDrawer} {...item} />
              ));
            })}
          </List>
        </nav>
      </Drawer>
    </>
  );
};

export default NavMenu;
